import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import type { SignatureEnvelope } from '../models/SignatureEnvelope';
import { logError } from '../../lib/utils';

export function useSignatureEnvelopes() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Creates a new signature envelope
   */
  const createEnvelope = async (
    data: {
      pdfFile?: string; // Base64 encoded PDF
      documentId?: string;
      clientId: string;
      organizationId: string;
      clientEmail: string;
      clientName?: string;
      companySignerUserId: string;
    }
  ): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const manageEnvelopeFn = httpsCallable(functions, 'manageEnvelope');
      const result = await manageEnvelopeFn({
        action: 'create',
        ...data,
      });
      return (result.data as any).envelopeId;
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.createEnvelope' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends envelope to client
   */
  const sendToClient = async (
    envelopeId: string,
    sendEmail: boolean = true
  ): Promise<{ signingLink: string; token: string }> => {
    setLoading(true);
    setError(null);

    try {
      const manageEnvelopeFn = httpsCallable(functions, 'manageEnvelope');
      const result = await manageEnvelopeFn({
        action: 'send',
        envelopeId,
        sendEmail,
      });
      return result.data as { signingLink: string; token: string };
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.sendToClient', envelopeId, sendEmail });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets envelope details
   */
  const getEnvelope = async (envelopeId: string): Promise<SignatureEnvelope> => {
    setLoading(true);
    setError(null);

    try {
      const manageEnvelopeFn = httpsCallable(functions, 'manageEnvelope');
      const result = await manageEnvelopeFn({
        action: 'get',
        envelopeId,
      });
      return result.data as SignatureEnvelope;
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.getEnvelope', envelopeId });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets signed download URL for final PDF
   */
  const getFinalPdf = async (envelopeId: string): Promise<{ downloadUrl: string; sha256?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const manageEnvelopeFn = httpsCallable(functions, 'manageEnvelope');
      const result = await manageEnvelopeFn({
        action: 'get',
        envelopeId,
        includePdf: true,
      });
      return {
        downloadUrl: (result.data as any).downloadUrl,
        sha256: (result.data as any).sha256,
      };
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.getFinalPdf', envelopeId });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validates signing token (public endpoint)
   */
  const validateSigningToken = async (token: string): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/clientSigningFlow?action=validate&token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate token');
      }

      return await response.json();
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.validateSigningToken' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends OTP code (public endpoint)
   */
  const sendOtp = async (envelopeId: string, email: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/clientSigningFlow`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'sendOtp',
            envelopeId,
            email,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.sendOtp', envelopeId });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifies OTP code (public endpoint)
   */
  const verifyOtp = async (envelopeId: string, email: string, code: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/clientSigningFlow`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'verifyOtp',
            envelopeId,
            email,
            code,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to verify OTP');
      }
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.verifyOtp', envelopeId });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submits client signature (public endpoint)
   */
  const submitClientSignature = async (
    token: string,
    signatureType: 'draw' | 'type',
    signatureImageUrl?: string,
    typedName?: string,
    signingDate?: string,
    consentGiven: boolean = true,
    otpCode?: string
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/clientSigningFlow`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'submit',
            token,
            signatureType,
            signatureImage: signatureImageUrl, // Backend expects signatureImage
            typedName,
            signingDate,
            consentGiven,
            otpCode,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit signature');
      }
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.submitClientSignature' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submits company signature
   */
  const submitCompanySignature = async (
    envelopeId: string,
    signatureType: 'draw' | 'type',
    signatureImage?: string,
    typedName?: string,
    signingDate?: string
  ): Promise<{ finalPdfUrl: string }> => {
    setLoading(true);
    setError(null);

    try {
      const submitCompanySignatureFn = httpsCallable(functions, 'submitCompanySignature');
      const result = await submitCompanySignatureFn({
        envelopeId,
        signatureType,
        signatureImage,
        typedName,
        signingDate,
      });
      return result.data as { finalPdfUrl: string };
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.submitCompanySignature', envelopeId });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Downloads signed PDF for client signer (public endpoint)
   */
  const downloadSignedPdf = async (token: string): Promise<{ downloadUrl: string }> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/downloadSignedPdf?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download signed PDF');
      }

      return await response.json();
    } catch (err: any) {
      const error = err as Error;
      setError(error);
      logError('signatures', err, { source: 'useSignatureEnvelopes.downloadSignedPdf' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createEnvelope,
    sendToClient,
    getEnvelope,
    getFinalPdf,
    validateSigningToken,
    sendOtp,
    verifyOtp,
    submitClientSignature,
    submitCompanySignature,
    downloadSignedPdf,
  };
}
